import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  calculateCartTotals,
  resolveTierLinePricing,
  type ProArticle,
  type ProArticlePricing,
  type ProCartLine,
  type ProCartTotals,
  type RemiseTier,
} from '@erp/domain/pro';

interface CartItem extends ProCartLine {
  /** Snapshot of display data so we don't have to re-fetch the article. */
  name: string;
  code: string;
  imageUrl: string | null;
}

/**
 * Produit que le catalogue ne contient pas, saisi librement.
 *
 * Une demande n'a NI article NI prix : elle ne pèse donc rien dans les totaux
 * et ne peut pas devenir une ligne de commande. Elle part avec un devis, et le
 * commerçant y répond depuis son tableau de bord.
 */
interface CartRequest {
  /** Identifiant local (le panier vit dans localStorage avant tout aller-retour serveur). */
  id: string;
  label: string;
  quantity: number;
  note: string | null;
}

interface CartContextValue {
  items: CartItem[];
  tier: RemiseTier;
  setTier: (t: RemiseTier) => void;
  /** Taux de commission du pro (%, fixé par l'administrateur). Exposé pour
   *  l'affichage par-ligne — le total vit déjà dans `totals.totalCommission`. */
  commissionRate: number;
  /** Adopte une nouvelle base de prix par défaut (changée depuis Paramètres) :
   *  efface le choix explicite du panier courant puis bascule le palier. Le
   *  repricing des lignes persistées suit via l'effet de resync du CartDrawer. */
  adoptDefaultTier: (t: RemiseTier) => void;
  addItem: (
    article: ProArticle,
    pricing: ProArticlePricing | null,
    quantity?: number,
  ) => void;
  updateQuantity: (articleId: string, quantity: number) => void;
  removeItem: (articleId: string) => void;
  clear: () => void;
  totals: ProCartTotals;
  /** Re-applies the chosen tier on every line (used when switching tier).
   *  Pass `explicit: true` when the change comes from a user gesture — the
   *  choice is then persisted and survives reloads (instead of being reset
   *  to the admin-assigned base tier). */
  applyTier: (
    tier: RemiseTier,
    pricingByArticle: Map<string, ProArticlePricing | null>,
    articleById: Map<string, ProArticle>,
    opts?: { explicit?: boolean },
  ) => void;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  itemCount: number;

  // ---- Produits hors catalogue -------------------------------------------
  requests: CartRequest[];
  addRequest: (input: { label: string; quantity: number; note?: string | null }) => void;
  updateRequest: (id: string, patch: Partial<Omit<CartRequest, 'id'>>) => void;
  removeRequest: (id: string) => void;
  /** Le panier contient au moins une demande → seul le devis est possible. */
  hasRequests: boolean;
}

const CartContext = createContext<CartContextValue | null>(null);

const CART_STORAGE_KEY = 'pro_cart_v1';
const CART_REQUESTS_KEY = 'pro_cart_requests_v1';
const TIER_STORAGE_KEY = 'pro_cart_tier_v1';
// Set ONLY when the user explicitly flips the Min/Moy/Max switch — wins over
// the admin-assigned base tier until the cart is cleared (order placed).
const TIER_CHOICE_KEY = 'pro_cart_tier_choice_v1';
const USER_PREFS_KEY = 'pro_user_prefs_v1';

function isRemiseTier(v: unknown): v is RemiseTier {
  return v === 'min' || v === 'moy' || v === 'max';
}

function readExplicitTierChoice(): RemiseTier | null {
  try {
    const raw = localStorage.getItem(TIER_CHOICE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isRemiseTier(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Initial tier: an explicit user choice (persisted by the cart's tier
 *  switch) wins, then the admin-assigned account base, then older local
 *  preferences, then 'moy' as a final default. */
function initialTier(defaultTier?: RemiseTier): RemiseTier {
  const explicit = readExplicitTierChoice();
  if (explicit) return explicit;
  if (defaultTier) return defaultTier;
  const cartTier = localStorage.getItem(TIER_STORAGE_KEY);
  if (cartTier) {
    try {
      const parsed: unknown = JSON.parse(cartTier);
      if (isRemiseTier(parsed)) return parsed;
    } catch {
      // fall through
    }
  }
  try {
    const prefsRaw = localStorage.getItem(USER_PREFS_KEY);
    if (prefsRaw) {
      const prefs = JSON.parse(prefsRaw) as { defaultRemiseTier?: RemiseTier };
      if (isRemiseTier(prefs.defaultRemiseTier)) {
        return prefs.defaultRemiseTier;
      }
    }
  } catch {
    // fall through
  }
  return 'moy';
}

export function CartProvider({
  children,
  commissionRate = 0,
  defaultTier,
}: {
  children: ReactNode;
  commissionRate?: number;
  defaultTier?: RemiseTier;
}) {
  const [items, setItems] = useState<CartItem[]>(() =>
    readStorage(CART_STORAGE_KEY, []),
  );
  const [requests, setRequests] = useState<CartRequest[]>(() =>
    readStorage<CartRequest[]>(CART_REQUESTS_KEY, []),
  );
  const [tier, setTierState] = useState<RemiseTier>(() => initialTier(defaultTier));
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(CART_REQUESTS_KEY, JSON.stringify(requests));
  }, [requests]);

  useEffect(() => {
    localStorage.setItem(TIER_STORAGE_KEY, JSON.stringify(tier));
  }, [tier]);

  // Adopt the admin-assigned base tier once the profile resolves — unless the
  // user explicitly chose a tier for this cart (choice survives reloads).
  useEffect(() => {
    if (defaultTier && !readExplicitTierChoice()) setTierState(defaultTier);
  }, [defaultTier]);

  const setTier = useCallback((t: RemiseTier) => setTierState(t), []);

  const adoptDefaultTier = useCallback((t: RemiseTier) => {
    try {
      localStorage.removeItem(TIER_CHOICE_KEY);
    } catch {
      /* noop */
    }
    setTierState(t);
  }, []);

  const addItem: CartContextValue['addItem'] = useCallback(
    (article, pricing, quantity = 1) => {
      setItems((prev) => {
        const existing = prev.find((it) => it.articleId === article.id);
        if (existing) {
          return prev.map((it) =>
            it.articleId === article.id
              ? { ...it, quantity: it.quantity + quantity }
              : it,
          );
        }
        const linePricing = resolveTierLinePricing(article, pricing, tier);
        return [
          ...prev,
          {
            articleId: article.id,
            quantity,
            unitHt: linePricing.unitHt,
            remisePct: linePricing.remisePct,
            vatRate: article.vatRate,
            purchasePrice: article.purchasePrice,
            name: article.name,
            code: article.code,
            imageUrl: article.imageUrl,
          },
        ];
      });
    },
    [tier],
  );

  const updateQuantity = useCallback((articleId: string, quantity: number) => {
    setItems((prev) =>
      prev
        .map((it) =>
          it.articleId === articleId
            ? { ...it, quantity: Math.max(0, quantity) }
            : it,
        )
        .filter((it) => it.quantity > 0),
    );
  }, []);

  const removeItem = useCallback((articleId: string) => {
    setItems((prev) => prev.filter((it) => it.articleId !== articleId));
  }, []);

  const addRequest: CartContextValue['addRequest'] = useCallback((input) => {
    const label = input.label.trim();
    if (!label) return;
    const quantity = Math.max(1, Math.floor(input.quantity) || 1);
    setRequests((prev) => [
      ...prev,
      {
        // crypto.randomUUID n'existe pas hors contexte sécurisé (LAN de test) —
        // le repli n'a besoin d'être unique que dans un panier.
        id:
          globalThis.crypto?.randomUUID?.() ??
          `req-${prev.length}-${performance.now().toString(36)}`,
        label,
        quantity,
        note: input.note?.trim() || null,
      },
    ]);
  }, []);

  const updateRequest: CartContextValue['updateRequest'] = useCallback(
    (id, patch) => {
      setRequests((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                ...patch,
                label: patch.label !== undefined ? patch.label : r.label,
                quantity:
                  patch.quantity !== undefined
                    ? Math.max(1, Math.floor(patch.quantity) || 1)
                    : r.quantity,
              }
            : r,
        ),
      );
    },
    [],
  );

  const removeRequest = useCallback((id: string) => {
    setRequests((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Clearing the cart (order placed / quote sent) also drops the explicit
  // tier choice: the next cart starts back at the admin-assigned base.
  const clear = useCallback(() => {
    setItems([]);
    setRequests([]);
    try {
      localStorage.removeItem(TIER_CHOICE_KEY);
    } catch {
      /* noop */
    }
    setTierState(defaultTier ?? 'moy');
  }, [defaultTier]);

  const applyTier: CartContextValue['applyTier'] = useCallback(
    (newTier, pricingByArticle, articleById, opts) => {
      if (opts?.explicit) {
        try {
          localStorage.setItem(TIER_CHOICE_KEY, JSON.stringify(newTier));
        } catch {
          /* noop */
        }
      }
      setTierState(newTier);
      setItems((prev) =>
        prev.map((it) => {
          const article = articleById.get(it.articleId);
          if (!article) return it;
          const pricing = pricingByArticle.get(it.articleId) ?? null;
          const linePricing = resolveTierLinePricing(article, pricing, newTier);
          return {
            ...it,
            unitHt: linePricing.unitHt,
            remisePct: linePricing.remisePct,
            vatRate: article.vatRate,
            purchasePrice: article.purchasePrice,
          };
        }),
      );
    },
    [],
  );

  const totals = useMemo(
    () => calculateCartTotals(items, commissionRate),
    [items, commissionRate],
  );

  // Une demande compte dans la pastille du panier : sinon un panier qui ne
  // contient QUE des demandes s'affiche vide et l'électricien croit les avoir
  // perdues.
  const itemCount = useMemo(
    () =>
      items.reduce((sum, it) => sum + it.quantity, 0) +
      requests.reduce((sum, r) => sum + r.quantity, 0),
    [items, requests],
  );

  return (
    <CartContext.Provider
      value={{
        items,
        tier,
        setTier,
        commissionRate,
        adoptDefaultTier,
        addItem,
        updateQuantity,
        removeItem,
        clear,
        totals,
        applyTier,
        drawerOpen,
        setDrawerOpen,
        itemCount,
        requests,
        addRequest,
        updateRequest,
        removeRequest,
        hasRequests: requests.length > 0,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

export type { CartItem, CartRequest };
